import { useRef, useState, useEffect, useContext } from "react";
import { SessionFilePayload } from "../../api/models";
import { SessionAttachment } from "../../types/uploads";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { Spinner, Panel, DefaultButton, SpinnerSize } from "@fluentui/react";
import readNDJSONStream from "ndjson-readablestream";

import appLogo from "../../assets/applogo.svg";
import styles from "./Chat.module.css";

import {
    attachStoredFileApi,
    chatApi,
    configApi,
    getChatHistoryApi,
    getCompanySharedDocumentsChatPayload,
    RetrievalMode,
    ChatAppResponse,
    ChatAppResponseOrError,
    ChatAppRequest,
    ChatProfileAnalytics,
    ResponseMessage,
    VectorFields,
    GPT4VInput,
    SpeechConfig,

} from "../../api";
import { AnalyticsFigure, AnalyticsFigureError, outputIntentFromApi } from "../../components/ProfileAnalyticsFlow";
import { extractMemoryApi } from "../../api/memoryApi";
import { Answer, AnswerError, AnswerLoading } from "../../components/Answer";
import { QuestionInput } from "../../components/QuestionInput";
import { UserChatMessage } from "../../components/UserChatMessage";
import { AnalysisPanel, AnalysisPanelTabs } from "../../components/AnalysisPanel";
import { HistoryProviderOptions, useHistoryManager } from "../../components/HistoryProviders";
import { SettingsButton } from "../../components/SettingsButton";
import { ClearChatButton } from "../../components/ClearChatButton";
import { useLogin, useMsalAuth, getToken, requireAccessControl } from "../../authConfig";
import { useMsal } from "@azure/msal-react";
import { TokenClaimsDisplay } from "../../components/TokenClaimsDisplay";
import { addChatToProjectApi } from "../../api/api";
import { LoginContext } from "../../loginContext";
import { Settings } from "../../components/Settings/Settings";
import { UploadedFilesDisplay } from "../../components/UploadedFilesDisplay";
import { useUploadedFiles } from "../../contexts/UploadedFilesContext";
import { DocumentFlow } from "../../components/DocumentFlow";
import { EndReportFlow } from "../../components/EndReportFlow";
import { FormFillFlow } from "../../components/FormTemplates/FormFillFlow";
import { HandlingSituationsFlow } from "../../components/HandlingSituationsFlow";
import { HomeTips } from "../../components/HomeTips";
import { SummaryFlow } from "../../components/SummaryFlow";
import { ReportModeMessage, ReportMessage } from "../../components/ReportModeMessage/ReportModeMessage";
import { useProjects } from "../../contexts/ProjectContext";
import { generateEndReportApi } from "../../api/api";
import { Project } from "../../api/models";

import { routeHome } from "../../features/homeWorkflows/api";
import { HomeWorkflowFlow } from "../../features/homeWorkflows/HomeWorkflowFlow";
import { ManualWorkflowChoice } from "../../features/homeWorkflows/ManualWorkflowChoice";
import { shouldOfferManualWorkflowChoice } from "../../features/homeWorkflows/manualChoiceVisibility";
import { chatRequestErrorFromPayload, chatRequestErrorFromResponse, chatRequestErrorKind } from "../../features/chat/chatRequestError";
import type { HomeFlow } from "../../features/homeWorkflows/models";
import { useBuilderIdentity } from "../../features/workflowBuilder/useBuilderIdentity";
import { sendWorkflowMessage } from "../../features/workflowBuilder/api";

type AnswerTuple = [user: string, response: ChatAppResponse, attachments?: SessionAttachment[]];



const getAnswerText = (answer: ChatAppResponse): string => {
    if (typeof answer.message.content === "string") {
        return answer.message.content;
    }
    try {
        return JSON.stringify(answer.message.content);
    } catch {
        return String(answer.message.content);
    }
};

const COMPANY_SHARED_SOURCE = "company_shared";
const MAX_SESSION_FILE_TEXT_CHARS = 120_000;

const parseUploadedAt = (value?: string): number => {
    if (!value) {
        return 0;
    }
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? 0 : parsed;
};

const sessionFileTextLength = (payload: SessionFilePayload): number => {
    if (typeof payload.text !== "string") {
        return 0;
    }
    return payload.text.length;
};

const mergeSessionFilePayloads = (
    userPayloads: SessionFilePayload[],
    companyPayloads: SessionFilePayload[]
): { merged: SessionFilePayload[]; dropped: SessionFilePayload[]; totalTextChars: number } => {
    const userTextChars = userPayloads.reduce((sum, payload) => sum + sessionFileTextLength(payload), 0);

    const companyCandidates = companyPayloads
        .filter(payload => typeof payload.text === "string" && payload.text.trim().length > 0)
        .map(payload => ({ ...payload, source: COMPANY_SHARED_SOURCE }))
        .sort((a, b) => parseUploadedAt(a.uploaded_at) - parseUploadedAt(b.uploaded_at)); // oldest first

    const keptCompanyPayloads = [...companyCandidates];
    const droppedCompanyPayloads: SessionFilePayload[] = [];

    let totalTextChars = userTextChars + keptCompanyPayloads.reduce((sum, payload) => sum + sessionFileTextLength(payload), 0);
    while (totalTextChars > MAX_SESSION_FILE_TEXT_CHARS && keptCompanyPayloads.length > 0) {
        const droppedPayload = keptCompanyPayloads.shift();
        if (!droppedPayload) {
            break;
        }
        droppedCompanyPayloads.push(droppedPayload);
        totalTextChars -= sessionFileTextLength(droppedPayload);
    }

    // Keep newest company docs first in the merged request payload.
    keptCompanyPayloads.sort((a, b) => parseUploadedAt(b.uploaded_at) - parseUploadedAt(a.uploaded_at));

    return {
        merged: [...userPayloads, ...keptCompanyPayloads],
        dropped: droppedCompanyPayloads,
        totalTextChars
    };
};

function chatMessageGptClassName(answer: ChatAppResponse): string {
    const base = styles.chatMessageGpt;
    return answer.profile_analytics ? `${base} ${styles.chatMessageGptWithAnalytics}` : base;
}

function ChatProfileAnalyticsDisplay({ analytics }: { analytics: ChatProfileAnalytics }) {
    const { t } = useTranslation();
    const intent = analytics.output_intent
        ? outputIntentFromApi(analytics.output_intent)
        : { wantTable: false, wantFigure: true };
    const showFigure = Boolean(intent.wantFigure && analytics.figure_png_base64);
    const showFigureError = Boolean(intent.wantFigure && analytics.figure_error && !analytics.figure_png_base64);

    if (!showFigure && !showFigureError) {
        return null;
    }

    return (
        <>
            {showFigure && <AnalyticsFigure base64={analytics.figure_png_base64!} />}
            {showFigureError && (
                <AnalyticsFigureError message={analytics.figure_error ?? t("projectPage.analytics.figureError")} />
            )}
        </>
    );
}

const Chat = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams<{ chatId?: string }>();
    const { chatId: urlChatId } = params;

    const [chatId, setChatId] = useState<string | undefined>(urlChatId);
    const [pendingSessionId, setPendingSessionId] = useState<string | undefined>(undefined);
    const [isPageLoading, setIsPageLoading] = useState(true);

    // Get project context from navigation state (if navigated from project)
    const locationState = location.state as {
        projectId?: string;
        projectName?: string;
        attachStoredFile?: { blob_path: string; filename: string; content_type: string };
    } | null;
    const [isHistoryLoading, setIsHistoryLoading] = useState(false);
    const [isConfigPanelOpen, setIsConfigPanelOpen] = useState(false);
    const [promptTemplate, setPromptTemplate] = useState<string>("");
    const [temperature, setTemperature] = useState<number>(0.3);
    const [seed, setSeed] = useState<number | null>(null);
    const [minimumRerankerScore, setMinimumRerankerScore] = useState<number>(0);
    const [minimumSearchScore, setMinimumSearchScore] = useState<number>(0);
    const [retrieveCount, setRetrieveCount] = useState<number>(3);
    const [maxSubqueryCount, setMaxSubqueryCount] = useState<number>(10);
    const [resultsMergeStrategy, setResultsMergeStrategy] = useState<string>("interleaved");
    const [retrievalMode, setRetrievalMode] = useState<RetrievalMode>(RetrievalMode.Hybrid);
    const [useSemanticRanker, setUseSemanticRanker] = useState<boolean>(true);
    const [useQueryRewriting, setUseQueryRewriting] = useState<boolean>(false);
    const [reasoningEffort, setReasoningEffort] = useState<string>("");
    const [streamingEnabled, setStreamingEnabled] = useState<boolean>(true);
    const [shouldStream, setShouldStream] = useState<boolean>(true);
    const [useSemanticCaptions, setUseSemanticCaptions] = useState<boolean>(false);
    const [includeCategory, setIncludeCategory] = useState<string>("");
    const [excludeCategory, setExcludeCategory] = useState<string>("");
    const [useSuggestFollowupQuestions, setUseSuggestFollowupQuestions] = useState<boolean>(false);
    const [vectorFields, setVectorFields] = useState<VectorFields>(VectorFields.Embedding);
    const [useOidSecurityFilter, setUseOidSecurityFilter] = useState<boolean>(true);
    const [useGroupsSecurityFilter, setUseGroupsSecurityFilter] = useState<boolean>(false);
    const [gpt4vInput, setGPT4VInput] = useState<GPT4VInput>(GPT4VInput.TextAndImages);
    const [useGPT4V, setUseGPT4V] = useState<boolean>(false);

    const lastQuestionRef = useRef<string>("");
    const chatMessageStreamEnd = useRef<HTMLDivElement | null>(null);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isStreaming, setIsStreaming] = useState<boolean>(false);
    const [error, setError] = useState<unknown>();
    const [currentQuestionAttachments, setCurrentQuestionAttachments] = useState<SessionAttachment[]>([]);

    const [activeCitation, setActiveCitation] = useState<string>();
    const [activeAnalysisPanelTab, setActiveAnalysisPanelTab] = useState<AnalysisPanelTabs | undefined>(undefined);

    const [selectedAnswer, setSelectedAnswer] = useState<number>(0);
    const [answers, setAnswers] = useState<AnswerTuple[]>([]);
    const [streamedAnswers, setStreamedAnswers] = useState<AnswerTuple[]>([]);
    const [speechUrls, setSpeechUrls] = useState<(string | null)[]>([]);

    const [showGPT4VOptions, setShowGPT4VOptions] = useState<boolean>(false);
    const [showSemanticRankerOption, setShowSemanticRankerOption] = useState<boolean>(false);
    const [showQueryRewritingOption, setShowQueryRewritingOption] = useState<boolean>(false);
    const [showReasoningEffortOption, setShowReasoningEffortOption] = useState<boolean>(false);
    const [showVectorOption, setShowVectorOption] = useState<boolean>(false);
    const [showSpeechInput, setShowSpeechInput] = useState<boolean>(true);
    const [showSpeechOutputBrowser, setShowSpeechOutputBrowser] = useState<boolean>(false);
    const [showSpeechOutputAzure, setShowSpeechOutputAzure] = useState<boolean>(false);
    const [showChatHistoryBrowser, setShowChatHistoryBrowser] = useState<boolean>(false);
    const [showChatHistoryCosmos, setShowChatHistoryCosmos] = useState<boolean>(false);
    const [showAgenticRetrievalOption, setShowAgenticRetrievalOption] = useState<boolean>(false);
    const [useAgenticRetrieval, setUseAgenticRetrieval] = useState<boolean>(false);
    const [showAdvancedSettings, setShowAdvancedSettings] = useState<boolean>(false);
    const [useDocumentSearch, setUseDocumentSearch] = useState<boolean>(false);
    const [documentFiles, setDocumentFiles] = useState<string[]>([]);
    const [companySharedSessionFiles, setCompanySharedSessionFiles] = useState<SessionFilePayload[]>([]);
    const [activeHomeTip, setActiveHomeTip] = useState<"none" | "document" | "handling" | "summary" | "endReport" | "formFill">("none");
    const [documentVoiceStartToken, setDocumentVoiceStartToken] = useState(0);

    type ReportStep = "idle" | "askProject" | "askTemplate" | "askExtra" | "generating" | "ready" | "error";
    const [reportMode, setReportMode] = useState<{
        step: ReportStep;
        projectId: string | null;
        template: string;
        extraContext: string;
        messages: ReportMessage[];
        anchorAnswerCount: number;
        attempt: number;
    }>({ step: "idle", projectId: null, template: "", extraContext: "", messages: [], anchorAnswerCount: 0, attempt: 0 });
    const [intelligenceMode, setIntelligenceMode] = useState(false);
    const reportBlobUrlRef = useRef<string | null>(null);

    const isHomeChat = !urlChatId && !locationState?.projectId;

    const audio = useRef(new Audio()).current;
    const [isPlaying, setIsPlaying] = useState(false);

    const chatInputRef = useRef<HTMLDivElement | null>(null);
    const [workflowComposerTarget, setWorkflowComposerTarget] = useState<HTMLDivElement | null>(null);
    
    // Uploaded files management
    const uploadedFilesManager = useUploadedFiles();
    const { projects: availableProjects, loadProjects } = useProjects();
    const buildAttachmentsFromHistory = (
        meta: any,
        attachedFileNames?: string[],
        legacyDataUrls?: { [filename: string]: string }
    ): SessionAttachment[] => {
        const attachmentsMap = new Map<string, SessionAttachment>();

        if (Array.isArray(meta)) {
            meta.forEach((item: any, index: number) => {
                if (!item) {
                    return;
                }

                const name = item.name ?? attachedFileNames?.[index] ?? `attachment-${index}`;
                const attachment: SessionAttachment = {
                    id: item.id ?? `${name}-${index}`,
                    name,
                    contentType: item.contentType ?? item.content_type ?? "",
                    size: item.size ?? 0,
                    text: item.text ?? "",
                    truncated: item.truncated ?? false,
                    dataUrl: item.dataUrl ?? item.data_url,
                    sasUrl: item.sasUrl ?? item.sas_url,
                    blobUrl: item.blobUrl ?? item.blob_url,
                    blobPath: item.blobPath ?? item.blob_path
                };

                attachmentsMap.set(name, attachment);
            });
        }

        if (Array.isArray(attachedFileNames)) {
            attachedFileNames.forEach((name, index) => {
                if (!attachmentsMap.has(name)) {
                    attachmentsMap.set(name, {
                        id: `${name}-${index}`,
                        name,
                        contentType: "",
                        size: 0,
                        text: "",
                        truncated: false,
                        dataUrl: undefined,
                        sasUrl: undefined,
                        blobUrl: undefined,
                        blobPath: undefined
                    });
                }
            });
        }

        if (legacyDataUrls) {
            Object.entries(legacyDataUrls).forEach(([name, dataUrl]) => {
                const attachment = attachmentsMap.get(name);
                if (attachment) {
                    attachment.dataUrl = attachment.dataUrl ?? dataUrl;
                } else {
                    attachmentsMap.set(name, {
                        id: `${name}-${attachmentsMap.size}`,
                        name,
                        contentType: "",
                        size: 0,
                        text: "",
                        truncated: false,
                        dataUrl
                    });
                }
            });
        }

        return Array.from(attachmentsMap.values());
    };

    const serializeAttachmentsForStorage = (attachments?: SessionAttachment[]): any[] => {
        if (!attachments || attachments.length === 0) {
            return [];
        }

        return attachments.map(att => {
            const stored: any = {
                id: att.id,
                name: att.name,
                contentType: att.contentType,
                size: att.size
            };

            if (att.sasUrl) {
                stored.sasUrl = att.sasUrl;
            }
            if (att.blobUrl) {
                stored.blobUrl = att.blobUrl;
            }
            if (att.blobPath) {
                stored.blobPath = att.blobPath;
            }

            return stored;
        });
    };

    const chatContainerRef = useRef<HTMLDivElement | null>(null);
    const [chatInputHeight, setChatInputHeight] = useState(0);

    useEffect(() => {
        const inputElement = workflowComposerTarget ?? chatInputRef.current;
        const updateHeight = () => {
            if (inputElement) {
                const height = lastQuestionRef.current || chatId ? inputElement.offsetHeight : 0;
                setChatInputHeight(height);
            }
        };

        // Set initial height
        updateHeight();

        // Watch for height changes
        const resizeObserver = new ResizeObserver(() => {
            updateHeight();
        });

        if (inputElement) {
            resizeObserver.observe(inputElement);
        }

        return () => {
            resizeObserver.disconnect();
        };
    }, [chatId, workflowComposerTarget]);

    useEffect(() => {
        if (chatContainerRef.current) {
            chatContainerRef.current.style.maxHeight = `calc(100vh - ${chatInputHeight}px)`;
        }
    }, [chatInputHeight]);

    const speechConfig: SpeechConfig = {
        speechUrls,
        setSpeechUrls,
        audio,
        isPlaying,
        setIsPlaying
    };

    const getConfig = async () => {
        configApi().then(config => {
            setShowGPT4VOptions(config.showGPT4VOptions);
            if (config.showGPT4VOptions) {
                setUseGPT4V(true);
            }
            setUseSemanticRanker(config.showSemanticRankerOption);
            setShowSemanticRankerOption(config.showSemanticRankerOption);
            setUseQueryRewriting(config.showQueryRewritingOption);
            setShowQueryRewritingOption(config.showQueryRewritingOption);
            setShowReasoningEffortOption(config.showReasoningEffortOption);
            setStreamingEnabled(config.streamingEnabled);
            if (!config.streamingEnabled) {
                setShouldStream(false);
            }
            if (config.showReasoningEffortOption) {
                setReasoningEffort(config.defaultReasoningEffort);
            }
            setShowVectorOption(config.showVectorOption);
            if (!config.showVectorOption) {
                setRetrievalMode(RetrievalMode.Text);
            }
            // Force voice input to be available in chat regardless of config
            setShowSpeechInput(true);
            setShowSpeechOutputBrowser(config.showSpeechOutputBrowser);
            setShowSpeechOutputAzure(config.showSpeechOutputAzure);
            setShowChatHistoryBrowser(config.showChatHistoryBrowser);
            setShowChatHistoryCosmos(config.showChatHistoryCosmos);
            setShowAgenticRetrievalOption(config.showAgenticRetrievalOption);
            setUseAgenticRetrieval(config.showAgenticRetrievalOption);
            if (config.showAgenticRetrievalOption) {
                setRetrieveCount(10);
            }
            setShowAdvancedSettings(config.showAdvancedSettings);
        });
    };

    const handleAsyncRequest = async (question: string, answers: [string, ChatAppResponse][], responseBody: ReadableStream<any>, attachments: SessionAttachment[] = []) => {
        let answer: string = "";
        let askResponse: ChatAppResponse = {
            message: { content: "", role: "assistant" },
            delta: { content: "", role: "assistant" },
            context: { data_points: [], followup_questions: null, thoughts: [] },
            session_state: null
        };

        const normalizeContent = (value: unknown) => {
            if (typeof value === "string") return value;
            if (value === null || value === undefined) return "";
            try {
                return JSON.stringify(value);
            } catch {
                return String(value);
            }
        };

        const updateState = (newContent: string) => {
            answer += newContent;
            const latestResponse: ChatAppResponse = {
                ...askResponse,
                message: { content: answer, role: askResponse.message.role }
            };
            setStreamedAnswers([...answers, [question, latestResponse, attachments.map(a => ({ ...a }))]]);
        };
        try {
            setIsStreaming(true);
            for await (const event of readNDJSONStream(responseBody)) {
                if (event["error"]) {
                    throw chatRequestErrorFromPayload(event);
                }

                if (event["delta"] && event["delta"]["role"]) {
                    askResponse.message = { ...askResponse.message, role: event["delta"]["role"] };
                }

                if (event["context"]) {
                    // Update context with new keys from latest event
                    askResponse.context = { ...askResponse.context, ...event["context"] };
                }

                if (event["session_state"]) {
                    askResponse.session_state = event["session_state"];
                }

                if (event["profile_analytics"]) {
                    askResponse.profile_analytics = event["profile_analytics"] as ChatProfileAnalytics;
                }

                if (event["message"]) {
                    const normalized = normalizeContent(event["message"].content);
                    askResponse.message = { ...event["message"], content: normalized };
                    if (!answer && normalized) {
                        setIsLoading(false);
                        answer = normalized;
                    }
                } else if (event["delta"] && event["delta"]["content"]) {
                    setIsLoading(false);
                    updateState(event["delta"]["content"]);
                }
            }
        } finally {
            setIsStreaming(false);
        }
        const finalContent = answer || normalizeContent(askResponse.message.content);
        const fullResponse: ChatAppResponse = {
            ...askResponse,
            message: { content: finalContent, role: askResponse.message.role || "assistant" }
        };
        return fullResponse;
    };

    const client = useMsalAuth ? useMsal().instance : undefined;
    const { loggedIn } = useContext(LoginContext);

    const historyProvider: HistoryProviderOptions = (() => {
        if (useLogin && showChatHistoryCosmos) return HistoryProviderOptions.CosmosDB;
        if (showChatHistoryBrowser) return HistoryProviderOptions.IndexedDB;
        return HistoryProviderOptions.None;
    })();
    const historyManager = useHistoryManager(historyProvider);

    useEffect(() => {
        let cancelled = false;

        const loadCompanySharedSessionFiles = async () => {
            if (!loggedIn) {
                if (!cancelled) {
                    setCompanySharedSessionFiles([]);
                }
                return;
            }

            try {
                const token = client ? await getToken(client) : undefined;
                const payloads = await getCompanySharedDocumentsChatPayload(token);
                if (!cancelled) {
                    setCompanySharedSessionFiles(payloads);
                }
                console.log(`Loaded ${payloads.length} company shared session payload file(s)`);
            } catch (error) {
                console.log("Failed to load company shared session payload files (non-critical):", error);
                if (!cancelled) {
                    setCompanySharedSessionFiles([]);
                }
            }
        };

        void loadCompanySharedSessionFiles();
        return () => {
            cancelled = true;
        };
    }, [loggedIn, client]);

    useEffect(() => {
        console.log("First useEffect triggered", { urlChatId, hasClient: !!client, pendingSessionId });

        setChatId(urlChatId);

        if (urlChatId && urlChatId !== pendingSessionId) {
            setPendingSessionId(undefined);
        }

        // Clear uploaded files when navigating between chats or to home
        uploadedFilesManager?.clearFiles();

        // Load chat history when navigating to an existing chat
        const loadChatHistory = async () => {
            console.log("First useEffect: loadChatHistory called", { urlChatId, hasClient: !!client, useMsalAuth });

            // Load chat history if we have a urlChatId
            // For MSAL auth, we need client. For custom auth, we don't (uses cookies)
            if (urlChatId) {
                try {
                    console.log("First useEffect: Starting to load chat history for:", urlChatId);
                    setIsPageLoading(true);

                    // Get token based on auth type
                    let token: string | undefined;
                    if (client) {
                        // MSAL auth
                        token = await getToken(client);
                        console.log("First useEffect: Got MSAL token:", token ? "present" : "missing");
                        if (!token) {
                            console.log("No token available, skipping chat history load");
                            setIsPageLoading(false);
                            return;
                        }
                    } else {
                        // Custom auth (uses cookies, so pass empty string)
                        token = "";
                        console.log("First useEffect: Using custom auth (cookie-based)");
                    }

                    console.log("First useEffect: About to call getChatHistoryApi with chatId:", urlChatId);
                    const history = await getChatHistoryApi(urlChatId, token);
                    console.log("First useEffect: Loaded chat history:", history);
                    console.log("First useEffect: history.answers:", history?.answers);
                    console.log("First useEffect: history.answers length:", history?.answers?.length);

                    // Convert history to the format expected by answers state
                    if (history && history.answers && history.answers.length > 0) {
                        console.log("First useEffect: Processing", history.answers.length, "messages");
                        const loadedAnswers: AnswerTuple[] = [];
                        for (const [question, response] of history.answers) {
                            let attachedFileNames: string[] | undefined;
                            let legacyDataUrls: { [filename: string]: string } | undefined;
                            let attachmentsMeta: any = undefined;

                            if (response && typeof response === 'object') {
                                const responseWithMeta = response as any;
                                if (responseWithMeta._attachedFiles) {
                                    attachedFileNames = responseWithMeta._attachedFiles;
                                }
                                if (responseWithMeta._attachmentDataUrls) {
                                    legacyDataUrls = responseWithMeta._attachmentDataUrls;
                                }
                                attachmentsMeta = responseWithMeta._sessionAttachments ?? responseWithMeta._session_attachments;
                            }

                            const responseContent = typeof response === 'string'
                                ? response
                                : (response && typeof response === 'object' && 'message' in response)
                                    ? (response as any).message.content
                                    : String(response);

                            const assistantResponse: ChatAppResponse = {
                                message: {
                                    content: responseContent,
                                    role: "assistant"
                                },
                                delta: {
                                    content: "",
                                    role: "assistant"
                                },
                                context: {
                                    data_points: [],
                                    followup_questions: null,
                                    thoughts: []
                                },
                                session_state: null
                            };

                            const attachments = buildAttachmentsFromHistory(attachmentsMeta, attachedFileNames, legacyDataUrls);
                            loadedAnswers.push([question, assistantResponse, attachments]);
                        }
                        console.log("First useEffect: About to set answers and lastQuestionRef");
                        setAnswers(loadedAnswers);
                        // Set lastQuestionRef to the last question so UI knows there's a conversation
                        if (loadedAnswers.length > 0) {
                            lastQuestionRef.current = loadedAnswers[loadedAnswers.length - 1][0];
                            console.log("First useEffect: Set lastQuestionRef to:", lastQuestionRef.current);
                        }
                    }
                } catch (error) {
                    console.error("Failed to load chat history:", error);
                } finally {
                    setIsPageLoading(false);
                }
            } else if (!urlChatId) {
                // Clear chat when no chatId
                setAnswers([]);
                setCurrentQuestionAttachments([]);
                uploadedFilesManager?.clearFiles();
            }
        };

        loadChatHistory();
    }, [urlChatId, client]);

    // Stored file being attached in the background (Search "Ask" button); drives
    // the upload animation in the question input while /files/attach runs.
    const [pendingStoredFile, setPendingStoredFile] = useState<{ blob_path: string; filename: string; content_type: string } | null>(null);

    // Pick up the attach intent passed via navigation state (Search "Ask" button),
    // then consume the state so back/refresh doesn't re-attach the file. Declared
    // after the effect above so the attach survives the clear-on-mount.
    useEffect(() => {
        const attachStoredFile = locationState?.attachStoredFile;
        if (!attachStoredFile) {
            return;
        }
        setPendingStoredFile(attachStoredFile);
        navigate(location.pathname, { replace: true, state: { ...locationState, attachStoredFile: undefined } });
    }, [locationState?.attachStoredFile]);

    // Fetch the stored file's session payload in the background and attach it.
    useEffect(() => {
        if (!pendingStoredFile) {
            return;
        }
        let cancelled = false;

        const attachStoredFile = async () => {
            try {
                const token = client ? await getToken(client) : "";
                const { session_file } = await attachStoredFileApi(
                    pendingStoredFile.blob_path,
                    pendingStoredFile.filename,
                    pendingStoredFile.content_type,
                    token
                );
                if (cancelled) {
                    return;
                }
                if (!session_file) {
                    throw new Error("Missing session file payload");
                }
                uploadedFilesManager?.addFile({
                    id: session_file.id,
                    name: session_file.name,
                    contentType: session_file.content_type,
                    size: session_file.size,
                    text: session_file.text || "",
                    truncated: session_file.truncated ?? false,
                    dataUrl: session_file.data_url,
                    sasUrl: session_file.sas_url,
                    blobUrl: session_file.blob_url,
                    blobPath: session_file.blob_path
                });
            } catch (error) {
                if (!cancelled) {
                    console.error("Failed to attach file to chat:", error);
                }
            } finally {
                if (!cancelled) {
                    setPendingStoredFile(null);
                }
            }
        };
        void attachStoredFile();

        return () => {
            cancelled = true;
        };
    }, [pendingStoredFile]);

    useEffect(() => {
        console.log("Second useEffect triggered", { chatId, pendingSessionId, urlChatId, hasClient: !!client });

        // IMPORTANT: Skip this useEffect if we loaded from URL (first useEffect handles it)
        // This useEffect is only for NEW chats created during the session (pendingSessionId)
        if (urlChatId) {
            console.log("Second useEffect: urlChatId exists, skipping (first useEffect handles this)");
            return;
        }

        if (!chatId) {
            console.log("Second useEffect: No chatId, clearing answers");
            setAnswers([]);
            setCurrentQuestionAttachments([]);
            lastQuestionRef.current = "";
            setPendingSessionId(undefined);
            setIsPageLoading(false);
            return;
        }

        if (chatId === pendingSessionId) {
            console.log("Second useEffect: chatId === pendingSessionId, skipping");
            return;
        }

        if (!client) {
            console.log("Second useEffect: No client, skipping");
            return;
        }

        console.log("Second useEffect: Loading from historyManager for chatId:", chatId);

        (async () => {
            try {
                setIsHistoryLoading(true);
                // Don't clear answers immediately - keep current state during loading

                const token = await getToken(client);
                const item = await historyManager.getItem(chatId, token);
                console.log("Second useEffect: historyManager returned:", item);

                if (item) {
                    // Convert old format to new format with proper structure
                    const itemsWithFiles = item.map(([question, response]) => {
                        let chatResponse: ChatAppResponse;
                        let attachedFileNames: string[] | undefined;
                        let legacyDataUrls: { [filename: string]: string } | undefined;
                        let attachmentsMeta: any = undefined;

                        if (typeof response === 'string') {
                            chatResponse = {
                                message: {
                                    content: response,
                                    role: "assistant"
                                },
                                delta: {
                                    content: "",
                                    role: "assistant"
                                },
                                context: {
                                    data_points: [],
                                    followup_questions: null,
                                    thoughts: []
                                },
                                session_state: chatId
                            };
                        } else if (response && typeof response === 'object') {
                            const responseWithMeta = response as any;
                            if (responseWithMeta._attachedFiles) {
                                attachedFileNames = responseWithMeta._attachedFiles;
                            }
                            if (responseWithMeta._attachmentDataUrls) {
                                legacyDataUrls = responseWithMeta._attachmentDataUrls;
                            }
                            attachmentsMeta = responseWithMeta._sessionAttachments ?? responseWithMeta._session_attachments;

                            chatResponse = {
                                message: response.message || { content: '', role: 'assistant' },
                                delta: response.delta || { content: '', role: 'assistant' },
                                context: response.context || {
                                    data_points: [],
                                    followup_questions: null,
                                    thoughts: []
                                },
                                session_state: response.session_state || chatId
                            };
                        } else {
                            chatResponse = {
                                message: {
                                    content: String(response),
                                    role: "assistant"
                                },
                                delta: {
                                    content: "",
                                    role: "assistant"
                                },
                                context: {
                                    data_points: [],
                                    followup_questions: null,
                                    thoughts: []
                                },
                                session_state: chatId
                            };
                        }

                        const attachments = buildAttachmentsFromHistory(attachmentsMeta, attachedFileNames, legacyDataUrls);
                        return [question, chatResponse, attachments] as AnswerTuple;
                    });
                    setAnswers(itemsWithFiles);

                    lastQuestionRef.current = item.length > 0 ? item[item.length - 1][0] : "";
                } else {
                    setAnswers([]);
                    setCurrentQuestionAttachments([]);
                    lastQuestionRef.current = "";
                }
            } catch (e) {
                console.error("Failed to load chat history", e);
                setAnswers([]);
                setCurrentQuestionAttachments([]);
                lastQuestionRef.current = "";
            } finally {
                setIsHistoryLoading(false);
            }
        })();
    }, [chatId, client, historyManager, pendingSessionId, urlChatId]);

    const extractMemoriesFromConversation = async (question: string, response: string, token?: string) => {
        try {
            if (!token) return;
            
            console.log("Extracting memories from conversation...");
            const conversation = `User: ${question}\n\nAssistant: ${response}`;
            await extractMemoryApi(conversation, token);
            console.log("Memory extraction completed");
        } catch (error) {
            console.log("Memory extraction failed (non-critical):", error);
        }
    };

    const homeIdentity = useBuilderIdentity();
    const [homeFlow, setHomeFlow] = useState<HomeFlow | null>(null);
    const homeRouting = useRef<AbortController | null>(null);
    const homeSending = useRef(false);
    const homeScope = useRef(homeIdentity.scope);
    homeScope.current = homeIdentity.scope;
    useEffect(() => {
        setHomeFlow(null);
        return () => homeRouting.current?.abort();
    }, [homeIdentity.scope, homeIdentity.switching]);

    const makeApiRequest = async (question: string, isWebSearch = false) => {
        if (homeSending.current || homeIdentity.switching) return;
        homeSending.current = true;
        lastQuestionRef.current = question;

        // Capture current files for this question
        const currentAttachments = (uploadedFilesManager?.files || []).map(file => ({ ...file }));

        const sessionFilePayloads: SessionFilePayload[] = currentAttachments.map(file => ({
            id: file.id,
            name: file.name,
            content_type: file.contentType,
            size: file.size,
            text: file.text || "",
            truncated: file.truncated,
            data_url: file.dataUrl?.replace(/\s+/g, ""),
            sas_url: file.sasUrl?.trim(),
            blob_url: file.blobUrl,
            blob_path: file.blobPath
        }));
        const {
            merged: mergedSessionFilePayloads,
            dropped: droppedCompanySharedPayloads,
            totalTextChars: mergedSessionTextChars
        } = mergeSessionFilePayloads(sessionFilePayloads, companySharedSessionFiles);

        if (droppedCompanySharedPayloads.length > 0) {
            const droppedNames = droppedCompanySharedPayloads.map(file => file.name).join(", ");
            console.warn(
                `Dropped ${droppedCompanySharedPayloads.length} company shared document(s) due to overflow: ${droppedNames}`
            );
        }
        if (mergedSessionTextChars > MAX_SESSION_FILE_TEXT_CHARS) {
            console.warn(
                "Session payload text exceeds safety cap after company-doc trimming; "
                + "sending request because user-attached files are preserved"
            );
        }
        setCurrentQuestionAttachments(currentAttachments);

        // Clear files from UI immediately when sending question (like ChatGPT)
        uploadedFilesManager?.clearFiles();

        error && setError(undefined);
        setIsLoading(true);
        setActiveCitation(undefined);
        setActiveAnalysisPanelTab(undefined);

        try {
            const token = client ? await getToken(client) : undefined;
            let workspaceCountIntent = false;
            // Explicit tools and attachment conversations keep their existing path.
            if (!locationState?.projectId && !isWebSearch && !intelligenceMode && currentAttachments.length === 0) {
                const scope = homeIdentity.scope;
                const abort = new AbortController();
                homeRouting.current?.abort();
                homeRouting.current = abort;
                const history = answers.slice(-6).flatMap(answer => [
                    { role: "user" as const, content: answer[0].slice(-16000) },
                    { role: "assistant" as const, content: getAnswerText(answer[1]).slice(-16000) }
                ]);
                const route = await routeHome(question, history, i18n.language, token, abort.signal);
                if (abort.signal.aborted || homeScope.current !== scope) return;
                if (route.action === "start" || route.action === "choose") {
                    setHomeFlow({ message: route.input, route });
                    return;
                }
                if (route.action === "create") {
                    const draft = await sendWorkflowMessage(undefined, {
                        request_id: crypto.randomUUID(), message: route.input, mode: "build", expected_revision: 1,
                        language: i18n.language, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
                    }, token, abort.signal);
                    if (!abort.signal.aborted && homeScope.current === scope) navigate(`/app/workflows/${encodeURIComponent(draft.id)}`);
                    return;
                }
                workspaceCountIntent = route.action === "answer"
                    && route.data_intent === "workspace_card_count"
                    && !useDocumentSearch
                    && documentFiles.length === 0;
            }
            const messages: ResponseMessage[] = answers.flatMap(a => [
                { content: a[0], role: "user" },
                { content: a[1].message.content, role: "assistant" }
            ]);

            const finalPromptTemplate = promptTemplate.length === 0 ? undefined : promptTemplate;
            
            // Debug logging to see what's being sent
            if (useDocumentSearch) {
                console.log("Document search enabled. Sending documents:", documentFiles);
            }

            // A routed Workspace count reads the live card index and needs no shared document payload.
            const requestSessionFiles = workspaceCountIntent ? [] : mergedSessionFilePayloads;
            // Debug logging for session files
            console.log("Session files to send:", requestSessionFiles);
            console.log("Session files count:", requestSessionFiles.length);
            if (requestSessionFiles.length > 0) {
                console.log("First session file:", requestSessionFiles[0]);
            }
            const companySharedCount = requestSessionFiles.filter(
                file => String(file.source || "").toLowerCase() === COMPANY_SHARED_SOURCE
            ).length;
            if (companySharedCount > 0) {
                console.log("Company shared session files included:", companySharedCount);
            }

            const latestSessionState = answers.length ? answers[answers.length - 1][1].session_state : null;

            const request: ChatAppRequest = {
                messages: [...messages, { content: question, role: "user" }],
                is_web_search: intelligenceMode && isHomeChat ? false : isWebSearch,
                use_document_search: useDocumentSearch,
                document_files: useDocumentSearch ? documentFiles : [],
                session_files: requestSessionFiles,
                context: {
                    overrides: {
                        prompt_template: finalPromptTemplate,
                        include_category: includeCategory.length === 0 ? undefined : includeCategory,
                        exclude_category: excludeCategory.length === 0 ? undefined : excludeCategory,
                        top: retrieveCount,
                        max_subqueries: maxSubqueryCount,
                        results_merge_strategy: resultsMergeStrategy,
                        temperature: temperature,
                        minimum_reranker_score: minimumRerankerScore,
                        minimum_search_score: minimumSearchScore,
                        retrieval_mode: retrievalMode,
                        semantic_ranker: useSemanticRanker,
                        semantic_captions: useSemanticCaptions,
                        query_rewriting: useQueryRewriting,
                        reasoning_effort: reasoningEffort,
                        suggest_followup_questions: useSuggestFollowupQuestions,
                        use_oid_security_filter: useOidSecurityFilter,
                        use_groups_security_filter: useGroupsSecurityFilter,
                        use_document_search: useDocumentSearch,
                        vector_fields: vectorFields,
                        use_gpt4v: useGPT4V,
                        gpt4v_input: gpt4vInput,
                        language: i18n.language,
                        use_agentic_retrieval: useAgenticRetrieval,
                        ...(workspaceCountIntent ? { data_intent: "workspace_card_count", skip_retrieval: true } : {}),
                        ...(intelligenceMode && isHomeChat ? { agent_type: "search" } : {}),
                        ...(seed !== null ? { seed: seed } : {}),
                        ...(locationState?.projectId ? { project_id: locationState.projectId } : {}),
                        ...(locationState?.projectId && typeof latestSessionState === "string"
                            ? { current_session_id: latestSessionState }
                            : {})
                    }
                },
                // AI Chat Protocol: Client must pass on any session state received from the server
                session_state: latestSessionState
            };

            const response = await chatApi(request, shouldStream, token);
            if (response.status > 299 || !response.ok) {
                throw await chatRequestErrorFromResponse(response);
            }
            if (!response.body) {
                throw Error("No response body");
            }
            if (shouldStream) {
                // Convert to old format for handleAsyncRequest
                const answersOldFormat = answers.map(([q, r]) => [q, r] as [string, ChatAppResponse]);
                const parsedResponse: ChatAppResponse = await handleAsyncRequest(question, answersOldFormat, response.body, currentAttachments);
                setAnswers([...answers, [question, parsedResponse, currentAttachments.map(file => ({ ...file }))]]);
                
                // Extract memories from the conversation
                const responseContent = typeof parsedResponse.message.content === 'string' 
                    ? parsedResponse.message.content 
                    : JSON.stringify(parsedResponse.message.content);
                void extractMemoriesFromConversation(question, responseContent, token);
                
                if (typeof parsedResponse.session_state === "string" && parsedResponse.session_state !== "") {
                    await handleSessionStateUpdate(parsedResponse.session_state, question, parsedResponse, currentAttachments);
                }
            } else {
                const parsedResponse: ChatAppResponseOrError = await response.json();
                if (parsedResponse.error) {
                    throw chatRequestErrorFromPayload(parsedResponse);
                }
                setAnswers([...answers, [question, parsedResponse as ChatAppResponse, currentAttachments.map(file => ({ ...file }))]]);

                // Extract memories from the conversation
                const responseContent = typeof (parsedResponse as ChatAppResponse).message.content === 'string'
                    ? (parsedResponse as ChatAppResponse).message.content
                    : JSON.stringify((parsedResponse as ChatAppResponse).message.content);
                void extractMemoriesFromConversation(question, responseContent, token);

                if (typeof parsedResponse.session_state === "string" && parsedResponse.session_state !== "") {
                    await handleSessionStateUpdate(parsedResponse.session_state, question, parsedResponse, currentAttachments);
                }
            }
            setSpeechUrls([...speechUrls, null]);
        } catch (e) {
            setError(e);
        } finally {
            homeSending.current = false;
            setIsLoading(false);
        }
    };

    const handleSessionStateUpdate = async (sessionId: string, question: string, response: ChatAppResponse, attachments: SessionAttachment[]) => {
        // Set both together to prevent useEffect from reloading
        setChatId(sessionId);
        setPendingSessionId(sessionId);

        const token = client ? await getToken(client) : undefined;

        // Store only essential data like ChatGPT (prevents object corruption)
        const cleanResponse = {
            message: {
                content: typeof response.message.content === 'string' ? response.message.content : String(response.message.content),
                role: response.message.role
            },
            context: response.context,
            session_state: response.session_state,
            delta: response.delta
        };

        // Store only file names (NOT data URLs) to avoid CosmosDB size limits
        const sanitizedAttachments = serializeAttachmentsForStorage(attachments);
        const attachmentNames = attachments.map(file => file.name);

        const responseWithMeta = {
            ...cleanResponse,
            _attachedFiles: attachmentNames,
            _sessionAttachments: sanitizedAttachments
        };

        const historyFormat = answers.map(([q, r, attachments]) => {
            if (attachments && attachments.length > 0) {
                const storedAttachments = serializeAttachmentsForStorage(attachments);
                return [q, { ...r, _attachedFiles: attachments.map(att => att.name), _sessionAttachments: storedAttachments } as any] as [string, ChatAppResponse];
            }
            return [q, r] as [string, ChatAppResponse];
        });

        historyFormat.push([question, responseWithMeta] as [string, ChatAppResponse]);

        await historyManager.addItem(sessionId, historyFormat, token);

        // Update URL without navigation (like ChatGPT does)
        // This changes the URL but doesn't cause any refresh or reload
        const newUrl = `/app/chats/${sessionId}`;
        if (window.location.pathname !== newUrl) {
            window.history.replaceState({}, '', newUrl);
        }

        // If this chat was started from a project, save it to the project
        if (locationState?.projectId && token) {
            try {
                await addChatToProjectApi(
                    locationState.projectId,
                    {
                        chat_id: sessionId,
                        chat_title: question.substring(0, 50),
                        last_message: typeof response.message.content === 'string'
                            ? response.message.content.substring(0, 100)
                            : String(response.message.content).substring(0, 100)
                    },
                    token
                );
                console.log("Chat saved to project:", locationState.projectId);
            } catch (error) {
                console.error("Failed to save chat to project:", error);
            }
        }
    };

    // ---------- Report Mode handlers ----------

    const makeReportMessageId = () => `rm-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    const handleReportModeActivate = () => {
        if (reportMode.step !== "idle") {
            return;
        }
        void loadProjects();
        const askProjectMsg: ReportMessage = {
            id: makeReportMessageId(),
            role: "ai",
            text: t("reportMode.askProject"),
            projects: availableProjects,
            onProjectSelected: handleReportProjectSelected
        };
        setReportMode(prev => ({
            ...prev,
            step: "askProject",
            anchorAnswerCount: answers.length,
            messages: [askProjectMsg]
        }));
    };

    const handleReportModeDismiss = () => {
        // Keep messages visible (user can still download); just exit the mode.
        setReportMode(prev => ({ ...prev, step: "idle" }));
    };

    const handleIntelligenceModeToggle = () => {
        setIntelligenceMode(prev => !prev);
    };

    const handleIntelligenceModeDismiss = () => {
        setIntelligenceMode(false);
    };

    const handleReportProjectSelected = (project: Project) => {
        setReportMode(prev => {
            const userMsg: ReportMessage = { id: makeReportMessageId(), role: "user", text: project.name };
            const askTemplateMsg: ReportMessage = {
                id: makeReportMessageId(),
                role: "ai",
                text: t("reportMode.askTemplate"),
                skipLabel: t("reportMode.skipTemplate"),
                onSkip: () => handleReportSkip("askTemplate")
            };
            // Replace project chips in the first AI message so the user can't re-pick.
            const updatedMessages = prev.messages.map(m =>
                m.projects ? { ...m, projects: undefined, onProjectSelected: undefined } : m
            );
            return {
                ...prev,
                step: "askTemplate",
                projectId: project.id,
                messages: [...updatedMessages, userMsg, askTemplateMsg]
            };
        });
    };

    const handleReportSkip = (fromStep: ReportStep) => {
        setReportMode(prev => {
            if (prev.step !== fromStep) return prev;
            // Strip ALL skip chips from prior messages — robust to soft-warning insertions.
            const stripped = prev.messages.map(m =>
                m.skipLabel || m.onSkip ? { ...m, skipLabel: undefined, onSkip: undefined } : m
            );
            if (fromStep === "askTemplate") {
                const userMsg: ReportMessage = { id: makeReportMessageId(), role: "user", text: t("reportMode.noTemplateUserLabel") };
                const askExtraMsg: ReportMessage = {
                    id: makeReportMessageId(),
                    role: "ai",
                    text: t("reportMode.askExtra"),
                    skipLabel: t("reportMode.skipExtra"),
                    onSkip: () => handleReportSkip("askExtra")
                };
                return { ...prev, step: "askExtra", template: "", messages: [...stripped, userMsg, askExtraMsg] };
            }
            if (fromStep === "askExtra") {
                const userMsg: ReportMessage = { id: makeReportMessageId(), role: "user", text: t("reportMode.nothingElseUserLabel") };
                const loadingMsg: ReportMessage = { id: makeReportMessageId(), role: "ai", text: "", isLoading: true };
                return { ...prev, step: "generating", extraContext: "", messages: [...stripped, userMsg, loadingMsg] };
            }
            return prev;
        });
    };

    const handleReportModeSend = (question: string): boolean | void => {
        const trimmed = question.trim();
        const files = uploadedFilesManager.files;
        const readableFiles = files.filter(f => f.text && f.text.trim().length > 0);
        const unreadableFiles = files.filter(f => !f.text || !f.text.trim());
        const hasTypedTemplate = trimmed.length > 0;

        // Hard block: askTemplate, all uploaded files unreadable, AND no typed description.
        if (
            reportMode.step === "askTemplate" &&
            unreadableFiles.length > 0 &&
            readableFiles.length === 0 &&
            !hasTypedTemplate
        ) {
            const fileNames = unreadableFiles.map(f => f.name).join(", ");
            const errorMsg: ReportMessage = {
                id: makeReportMessageId(),
                role: "ai",
                text: t("reportMode.templateUnreadableHard", { files: fileNames })
            };
            setReportMode(prev => ({ ...prev, messages: [...prev.messages, errorMsg] }));
            uploadedFilesManager.clearFiles();
            return false;
        }

        const filesText = readableFiles
            .map(f => `File: ${f.name}\n${f.text}`)
            .join("\n\n");
        const attachmentsSnapshot = files.map(f => ({ ...f }));

        setReportMode(prev => {
            // Strip ALL skip chips from prior messages — safer than indexing "last message"
            // because soft-warning insertions can shift the index.
            const stripped = prev.messages.map(m =>
                m.skipLabel || m.onSkip ? { ...m, skipLabel: undefined, onSkip: undefined } : m
            );

            if (prev.step === "askTemplate") {
                const template = [trimmed, filesText].filter(Boolean).join("\n\n");
                const newMessages: ReportMessage[] = [...stripped];

                if (unreadableFiles.length > 0) {
                    newMessages.push({
                        id: makeReportMessageId(),
                        role: "ai",
                        text: t("reportMode.templateUnreadableSoft", {
                            files: unreadableFiles.map(f => f.name).join(", ")
                        })
                    });
                }
                newMessages.push({
                    id: makeReportMessageId(),
                    role: "user",
                    text: trimmed,
                    attachments: attachmentsSnapshot.length > 0 ? attachmentsSnapshot : undefined
                });
                newMessages.push({
                    id: makeReportMessageId(),
                    role: "ai",
                    text: t("reportMode.askExtra"),
                    skipLabel: t("reportMode.skipExtra"),
                    onSkip: () => handleReportSkip("askExtra")
                });
                return { ...prev, step: "askExtra", template, messages: newMessages };
            }
            if (prev.step === "askExtra") {
                return {
                    ...prev,
                    step: "generating",
                    extraContext: trimmed,
                    messages: [
                        ...stripped,
                        { id: makeReportMessageId(), role: "user", text: trimmed },
                        { id: makeReportMessageId(), role: "ai", text: "", isLoading: true }
                    ]
                };
            }
            return prev;
        });

        if (uploadedFilesManager.files.length > 0) {
            uploadedFilesManager.clearFiles();
        }
    };

    const handleReportRetry = () => {
        setReportMode(prev => {
            const updated = prev.messages.map((m, i) =>
                i === prev.messages.length - 1 ? { ...m, isError: false, onRetry: undefined, text: "", isLoading: true } : m
            );
            return { ...prev, step: "generating", messages: updated, attempt: prev.attempt + 1 };
        });
    };

    // Generation effect: triggered by step "generating".
    useEffect(() => {
        if (reportMode.step !== "generating" || !reportMode.projectId) return;

        const controller = new AbortController();
        let cancelled = false;

        const run = async () => {
            let token: string | undefined = "";
            try {
                if (useMsalAuth && client) {
                    token = await getToken(client);
                }
            } catch (authError) {
                console.error("Failed to resolve auth token:", authError);
            }

            try {
                const response = await generateEndReportApi(reportMode.projectId!, token, controller.signal, {
                    user_template: reportMode.template,
                    extra_context: reportMode.extraContext,
                    mode: reportMode.template.trim() ? "template_driven" : "fixed"
                });
                const blob = await response.blob();
                if (cancelled) return;

                const disposition = response.headers.get("Content-Disposition") || "";
                const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
                const filename = match ? decodeURIComponent(match[1]) : "report.docx";

                if (reportBlobUrlRef.current) {
                    URL.revokeObjectURL(reportBlobUrlRef.current);
                }
                const newBlobUrl = URL.createObjectURL(blob);
                reportBlobUrlRef.current = newBlobUrl;

                setReportMode(prev => {
                    const updated = prev.messages.map((m, i) =>
                        i === prev.messages.length - 1
                            ? {
                                  ...m,
                                  isLoading: false,
                                  text: t("reportMode.readyMessage"),
                                  download: { blobUrl: newBlobUrl, filename }
                              }
                            : m
                    );
                    return { ...prev, step: "ready", messages: updated };
                });
            } catch (error) {
                if (controller.signal.aborted || cancelled) return;
                const message = error instanceof Error ? error.message : t("reportMode.error");
                setReportMode(prev => {
                    const updated = prev.messages.map((m, i) =>
                        i === prev.messages.length - 1
                            ? { ...m, isLoading: false, text: message, isError: true, onRetry: handleReportRetry }
                            : m
                    );
                    return { ...prev, step: "error", messages: updated };
                });
            }
        };

        run();

        return () => {
            cancelled = true;
            controller.abort();
        };
    }, [reportMode.step, reportMode.attempt, reportMode.projectId]);

    // Refresh project chip data once projects load (the activate path may have fired before they arrived).
    useEffect(() => {
        if (reportMode.step !== "askProject") return;
        setReportMode(prev => {
            if (prev.step !== "askProject") return prev;
            const askText = t("reportMode.askProject");
            const noProjectsText = t("reportMode.noProjects");
            const updated = prev.messages.map(m => {
                if (m.role !== "ai" || (m.text !== askText && m.text !== noProjectsText)) return m;
                if (availableProjects.length === 0) {
                    return { ...m, text: noProjectsText, projects: undefined, onProjectSelected: undefined };
                }
                return { ...m, text: askText, projects: availableProjects, onProjectSelected: handleReportProjectSelected };
            });
            return { ...prev, messages: updated };
        });
    }, [availableProjects.length, reportMode.step]);

    // Cleanup blob URL on unmount
    useEffect(() => {
        return () => {
            if (reportBlobUrlRef.current) {
                URL.revokeObjectURL(reportBlobUrlRef.current);
                reportBlobUrlRef.current = null;
            }
        };
    }, []);

    const clearChat = async () => {
        setHomeFlow(null);
        lastQuestionRef.current = "";
        error && setError(undefined);
        setActiveCitation(undefined);
        setActiveAnalysisPanelTab(undefined);
        setAnswers([]);
        setSpeechUrls([]);
        setStreamedAnswers([]);
        setDocumentFiles([]);
        setUseDocumentSearch(false);
        setCurrentQuestionAttachments([]);
        setIsLoading(false);
        setIsStreaming(false);
        setActiveHomeTip("none");

        // Delete files when clearing conversation (like ChatGPT starting new conversation)
        uploadedFilesManager?.clearFiles();

        // Reset report mode
        if (reportBlobUrlRef.current) {
            URL.revokeObjectURL(reportBlobUrlRef.current);
            reportBlobUrlRef.current = null;
        }
        setReportMode({ step: "idle", projectId: null, template: "", extraContext: "", messages: [], anchorAnswerCount: 0, attempt: 0 });
    };

    const newChat = async () => {
        // Delete files from backend and clear from UI when starting new chat
        uploadedFilesManager?.clearFiles();

        navigate("/app");
        setChatId(undefined);
        setPendingSessionId(undefined);
        await clearChat();
    };

    // Braces matter: a concise arrow would return whatever scrollIntoView yields, and
    // React treats that as the cleanup function. It is undefined per spec, but anything
    // that patches Element.prototype.scrollIntoView (browser extensions do) can make it
    // a Promise, and React then throws "destroy is not a function" on the next run.
    useEffect(() => {
        chatMessageStreamEnd.current?.scrollIntoView({ behavior: "smooth" });
    }, [isLoading]);
    useEffect(() => {
        chatMessageStreamEnd.current?.scrollIntoView({ behavior: "auto" });
    }, [streamedAnswers]);
    useEffect(() => {
        getConfig();
    }, []);

    // Clear chat when navigating to home (no chatId in URL)
    useEffect(() => {
        if (!urlChatId) {
            // User is on home page, ensure chat is cleared
            if (chatId || answers.length > 0) {
                clearChat();
                setChatId(undefined);
                setPendingSessionId(undefined);
            }
        }
    }, [urlChatId]);

    // Listen for clearChat event from sidebar
    useEffect(() => {
        const handleClearChat = () => {
            clearChat();
            setChatId(undefined);
            setPendingSessionId(undefined);
        };

        window.addEventListener("clearChat", handleClearChat);
        return () => window.removeEventListener("clearChat", handleClearChat);
    }, []);

    // Note: Project-based chats are now handled directly in the Project.tsx page
    // No need to auto-send questions from navigation state

    const handleSettingsChange = (field: string, value: any) => {
        switch (field) {
            case "promptTemplate":
                setPromptTemplate(value);
                break;
            case "temperature":
                setTemperature(value);
                break;
            case "seed":
                setSeed(value);
                break;
            case "minimumRerankerScore":
                setMinimumRerankerScore(value);
                break;
            case "minimumSearchScore":
                setMinimumSearchScore(value);
                break;
            case "retrieveCount":
                setRetrieveCount(value);
                break;
            case "maxSubqueryCount":
                setMaxSubqueryCount(value);
                break;
            case "resultsMergeStrategy":
                setResultsMergeStrategy(value);
                break;
            case "useSemanticRanker":
                setUseSemanticRanker(value);
                break;
            case "useQueryRewriting":
                setUseQueryRewriting(value);
                break;
            case "reasoningEffort":
                setReasoningEffort(value);
                break;
            case "useSemanticCaptions":
                setUseSemanticCaptions(value);
                break;
            case "excludeCategory":
                setExcludeCategory(value);
                break;
            case "includeCategory":
                setIncludeCategory(value);
                break;
            case "useOidSecurityFilter":
                setUseOidSecurityFilter(value);
                break;
            case "useGroupsSecurityFilter":
                setUseGroupsSecurityFilter(value);
                break;
            case "shouldStream":
                setShouldStream(value);
                break;
            case "useSuggestFollowupQuestions":
                setUseSuggestFollowupQuestions(value);
                break;
            case "useGPT4V":
                setUseGPT4V(value);
                break;
            case "gpt4vInput":
                setGPT4VInput(value);
                break;
            case "vectorFields":
                setVectorFields(value);
                break;
            case "retrievalMode":
                setRetrievalMode(value);
                break;
            case "useAgenticRetrieval":
                setUseAgenticRetrieval(value);
        }
    };

    const onShowCitation = (citation: string, index: number) => {
        if (activeCitation === citation && activeAnalysisPanelTab === AnalysisPanelTabs.CitationTab && selectedAnswer === index) {
            setActiveAnalysisPanelTab(undefined);
        } else {
            setActiveCitation(citation);
            setActiveAnalysisPanelTab(AnalysisPanelTabs.CitationTab);
        }

        setSelectedAnswer(index);
    };

    const onToggleTab = (tab: AnalysisPanelTabs, index: number) => {
        if (activeAnalysisPanelTab === tab && selectedAnswer === index) {
            setActiveAnalysisPanelTab(undefined);
        } else {
            setActiveAnalysisPanelTab(tab);
        }

        setSelectedAnswer(index);
    };

    const { t, i18n } = useTranslation();

    const scrollToBottom = () => {
        chatMessageStreamEnd.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        if (!isPageLoading) {
            scrollToBottom();
        }
    }, [answers, streamedAnswers, isLoading, error]);

    useEffect(() => {
        chatMessageStreamEnd.current?.scrollIntoView({ behavior: "auto" });
    }, [isPageLoading]);

    const hasReportMessages = reportMode.messages.length > 0;
    const reportModeUiActive = reportMode.step !== "idle";
    const isEmptyState = !isPageLoading && !isHistoryLoading && !lastQuestionRef.current && !hasReportMessages;
    const conversationStarted = !!chatId || !!lastQuestionRef.current || hasReportMessages;
    const reportSendDisabled = reportMode.step === "askProject" || reportMode.step === "generating";
    const openDocumentVoiceTip = () => {
        setDocumentVoiceStartToken(token => token + 1);
        setActiveHomeTip("document");
    };

    const renderChatInput = (additionalClassNames: string[] = []) => {
        const inputClasses = [
            styles.chatInput,
            conversationStarted ? styles.chatFixed : "",
            activeAnalysisPanelTab ? styles.chatInputWithPanel : "",
            ...additionalClassNames
        ]
            .filter(Boolean)
            .join(" ");

        return (
            <div ref={chatInputRef} className={inputClasses}>
                <UploadedFilesDisplay
                    files={uploadedFilesManager.files}
                    onRemoveFile={uploadedFilesManager.removeFile}
                />
                <QuestionInput
                    clearOnSend
                    placeholder={
                        intelligenceMode && isHomeChat
                            ? t("homeChat.intelligence.placeholder")
                            : t("defaultExamples.placeholder")
                    }
                    disabled={isLoading || reportSendDisabled}
                    onSend={(question, isWebSearch): boolean | void => {
                        if (reportMode.step === "askTemplate" || reportMode.step === "askExtra") {
                            return handleReportModeSend(question);
                        }
                        void makeApiRequest(question, isWebSearch);
                    }}
                    showSpeechInput={showSpeechInput}
                    uploadedFilesManager={uploadedFilesManager}
                    onDocumentSearchToggle={setUseDocumentSearch}
                    useDocumentSearch={useDocumentSearch}
                    documentFiles={documentFiles}
                    onDocumentFilesChange={setDocumentFiles}
                    reportModeActive={reportModeUiActive}
                    onReportModeToggle={handleReportModeActivate}
                    onReportModeDismiss={handleReportModeDismiss}
                    allowSendWithoutText={reportMode.step === "askTemplate"}
                    externalUploadingFileName={pendingStoredFile?.filename}
                    {...(isHomeChat
                        ? {
                              intelligenceModeActive: intelligenceMode,
                              onIntelligenceModeToggle: handleIntelligenceModeToggle,
                              onIntelligenceModeDismiss: handleIntelligenceModeDismiss,
                              onWorkflowModeOpen: () => navigate("/app/workflows/new")
                          }
                        : {})}
                />
            </div>
        );
    };

    const renderManualWorkflowChoice = (question: string, answer: ChatAppResponse, attachments: readonly unknown[] | undefined, index: number) => {
        if (locationState?.projectId || intelligenceMode || reportModeUiActive || attachments?.length || homeIdentity.switching
            || !shouldOfferManualWorkflowChoice(answer)) return null;
        return <ManualWorkflowChoice
            key={`${homeIdentity.scope}:${index}`}
            disabled={isLoading || isStreaming || homeFlow !== null}
            onSelect={candidate => {
                if (homeSending.current || homeIdentity.switching) return;
                setActiveAnalysisPanelTab(undefined);
                setActiveCitation(undefined);
                setHomeFlow({ message: question, route: {
                    action: "start", input: question, question: "", project_id: null, candidates: [candidate]
                } });
                requestAnimationFrame(() => chatMessageStreamEnd.current?.scrollIntoView({ behavior: "smooth" }));
            }}
        />;
    };

    const conversationContent = (() => {
        if (isPageLoading) {
            return (
                <div className={styles.pageLoadingContainer}>
                    <Spinner size={SpinnerSize.large} />
                </div>
            );
        }

        if (isHistoryLoading) {
            return (
                <div className={styles.pageLoadingContainer}>
                    {/* Blank loading state for ChatGPT-style loading */}
                </div>
            );
        }

        if (isEmptyState) {
            return (
                <div className={`${styles.chatEmptyState} ${styles.chatEmptyStateHero}`}>
                    <img src={appLogo} className={styles.appLogo} alt="App logo" />
                    <h1 className={styles.chatEmptyStateTitle}>{t("chatEmptyStateTitle")}</h1>
                </div>
            );
        }

        return (
            <div className={styles.chatMessageStream}>
                <div className={styles.messagesContainer}>
                    {isStreaming &&
                        streamedAnswers.map((streamedAnswer, index) => (
                            <div key={index}>
                                <UserChatMessage message={streamedAnswer[0]} attachments={streamedAnswer[2]} />
                                <div className={chatMessageGptClassName(streamedAnswer[1])}>
                                    <Answer
                                        isStreaming={true}
                                        key={index}
                                        answer={streamedAnswer[1]}
                                        index={index}
                                        speechConfig={speechConfig}
                                        isSelected={false}
                                        onCitationClicked={c => onShowCitation(c, index)}
                                        onThoughtProcessClicked={() => onToggleTab(AnalysisPanelTabs.ThoughtProcessTab, index)}
                                        onSupportingContentClicked={() => onToggleTab(AnalysisPanelTabs.SupportingContentTab, index)}
                                        onFollowupQuestionClicked={q => makeApiRequest(q)}
                                        showFollowupQuestions={useSuggestFollowupQuestions && answers.length - 1 === index}
                                        showSpeechOutputAzure={showSpeechOutputAzure}
                                        showSpeechOutputBrowser={showSpeechOutputBrowser}
                                    />
                                    {streamedAnswer[1].profile_analytics && (
                                        <ChatProfileAnalyticsDisplay analytics={streamedAnswer[1].profile_analytics} />
                                    )}
                                </div>
                            </div>
                        ))}
                    {!isStreaming &&
                        answers.slice(0, reportMode.anchorAnswerCount).map((answer, index) => (
                            <div key={index}>
                                <UserChatMessage message={answer[0]} attachments={answer[2]} />
                                <div className={chatMessageGptClassName(answer[1])}>
                                    <Answer
                                        isStreaming={false}
                                        key={index}
                                        answer={answer[1]}
                                        index={index}
                                        speechConfig={speechConfig}
                                        isSelected={selectedAnswer === index && activeAnalysisPanelTab !== undefined}
                                        onCitationClicked={c => onShowCitation(c, index)}
                                        onThoughtProcessClicked={() => onToggleTab(AnalysisPanelTabs.ThoughtProcessTab, index)}
                                        onSupportingContentClicked={() => onToggleTab(AnalysisPanelTabs.SupportingContentTab, index)}
                                        onFollowupQuestionClicked={q => makeApiRequest(q)}
                                        showFollowupQuestions={useSuggestFollowupQuestions && answers.length - 1 === index}
                                        showSpeechOutputAzure={showSpeechOutputAzure}
                                        showSpeechOutputBrowser={showSpeechOutputBrowser}
                                    />
                                    {answer[1].profile_analytics && (
                                        <ChatProfileAnalyticsDisplay analytics={answer[1].profile_analytics} />
                                    )}
                                </div>
                                {renderManualWorkflowChoice(answer[0], answer[1], answer[2], index)}
                            </div>
                        ))}
                    {hasReportMessages && reportMode.messages.map(msg => (
                        <ReportModeMessage key={msg.id} msg={msg} />
                    ))}
                    {!isStreaming &&
                        answers.slice(reportMode.anchorAnswerCount).map((answer, sliceIndex) => {
                            const index = reportMode.anchorAnswerCount + sliceIndex;
                            return (
                                <div key={index}>
                                    <UserChatMessage message={answer[0]} attachments={answer[2]} />
                                    <div className={chatMessageGptClassName(answer[1])}>
                                        <Answer
                                            isStreaming={false}
                                            key={index}
                                            answer={answer[1]}
                                            index={index}
                                            speechConfig={speechConfig}
                                            isSelected={selectedAnswer === index && activeAnalysisPanelTab !== undefined}
                                            onCitationClicked={c => onShowCitation(c, index)}
                                            onThoughtProcessClicked={() => onToggleTab(AnalysisPanelTabs.ThoughtProcessTab, index)}
                                            onSupportingContentClicked={() => onToggleTab(AnalysisPanelTabs.SupportingContentTab, index)}
                                            onFollowupQuestionClicked={q => makeApiRequest(q)}
                                            showFollowupQuestions={useSuggestFollowupQuestions && answers.length - 1 === index}
                                            showSpeechOutputAzure={showSpeechOutputAzure}
                                            showSpeechOutputBrowser={showSpeechOutputBrowser}
                                        />
                                        {answer[1].profile_analytics && (
                                            <ChatProfileAnalyticsDisplay analytics={answer[1].profile_analytics} />
                                        )}
                                    </div>
                                    {renderManualWorkflowChoice(answer[0], answer[1], answer[2], index)}
                                </div>
                            );
                        })}
                    {isLoading && (
                        <>
                            <UserChatMessage message={lastQuestionRef.current} attachments={currentQuestionAttachments} />
                            <div className={styles.chatMessageGptMinWidth}>
                                <AnswerLoading />
                            </div>
                        </>
                    )}
                    {error ? (
                        <>
                            <UserChatMessage message={lastQuestionRef.current} attachments={currentQuestionAttachments} />
                            <div className={styles.chatMessageGptMinWidth}>
                                <AnswerError
                                    error={t(`chatRequest.${chatRequestErrorKind(error)}`)}
                                    retryLabel={t("chatRequest.retry")}
                                    onRetry={() => makeApiRequest(lastQuestionRef.current)}
                                />
                            </div>
                        </>
                    ) : null}
                    {homeFlow && !homeIdentity.switching && (
                        <HomeWorkflowFlow key={homeIdentity.scope} flow={homeFlow} composerTarget={workflowComposerTarget} onClose={() => {
                            setHomeFlow(null);
                            if (!answers.length) lastQuestionRef.current = "";
                        }} />
                    )}
                    <div ref={chatMessageStreamEnd} />
                </div>
            </div>
        );
    })();


    return (
        <div className={styles.container}>
            {/* Setting the page title using react-helmet-async */}
            <Helmet>
                <title>{t("pageTitle")}</title>
            </Helmet>
            {showAdvancedSettings && (
                <div className={styles.commandsSplitContainer}>
                    <div className={styles.commandsContainer}>
                        <ClearChatButton className={styles.commandButton} onClick={() => clearChat()} disabled={(!lastQuestionRef.current && !hasReportMessages) || isLoading} />
                        <SettingsButton className={styles.commandButton} onClick={() => setIsConfigPanelOpen(!isConfigPanelOpen)} />
                    </div>
                </div>
            )}
            <div className={styles.chatRoot}>
                <div
                    ref={chatContainerRef}
                    className={`${styles.chatContainer} ${conversationStarted ? styles.heightFull : ""} ${
                        // While a home tip is open the flow brings its own scroller;
                        // letting this container grow past the viewport was the
                        // second scrollbar.
                        isEmptyState && activeHomeTip === "none" ? styles.chatContainerEmpty : ""
                    }`}
                >
                    {isEmptyState ? (
                        <div className={`${styles.chatEmptyStateGroup} ${activeHomeTip !== "none" ? styles.chatEmptyStateGroupDocumentActive : ""}`}>
                            {activeHomeTip === "none" && conversationContent}
                            {activeHomeTip === "document" && (
                                <DocumentFlow
                                    onDismiss={() => setActiveHomeTip("none")}
                                    autoStartVoiceToken={documentVoiceStartToken > 0 ? documentVoiceStartToken : undefined}
                                />
                            )}
                            {activeHomeTip === "handling" && (
                                <HandlingSituationsFlow onDismiss={() => setActiveHomeTip("none")} />
                            )}
                            {activeHomeTip === "summary" && (
                                <SummaryFlow onDismiss={() => setActiveHomeTip("none")} />
                            )}
                            {activeHomeTip === "endReport" && (
                                <EndReportFlow onDismiss={() => setActiveHomeTip("none")} />
                            )}
                            {activeHomeTip === "formFill" && (
                                <FormFillFlow onDismiss={() => setActiveHomeTip("none")} />
                            )}
                            {renderChatInput(activeHomeTip !== "none" ? [styles.chatInputHidden] : [])}
                            {activeHomeTip === "none" && (
                                <HomeTips
                                    onDocumentTipClicked={() => {
                                        setDocumentVoiceStartToken(0);
                                        setActiveHomeTip("document");
                                    }}
                                    onDocumentVoiceTipClicked={openDocumentVoiceTip}
                                    onHandlingTipClicked={() => setActiveHomeTip("handling")}
                                    onFormFillTipClicked={() => setActiveHomeTip("formFill")}
                                />
                            )}
                        </div>
                    ) : (
                        <>
                            {conversationContent}
                            {homeFlow && !homeIdentity.switching ? (
                                <div ref={setWorkflowComposerTarget} className={`${styles.chatInput} ${styles.chatFixed}`} />
                            ) : renderChatInput()}
                        </>
                    )}
                </div>

                {answers.length > 0 && activeAnalysisPanelTab && (
                    <AnalysisPanel
                        className={styles.chatAnalysisPanel}
                        activeCitation={activeCitation}
                        onActiveTabChanged={x => onToggleTab(x, selectedAnswer)}
                        citationHeight="810px"
                        answer={answers[selectedAnswer][1]}
                        activeTab={activeAnalysisPanelTab}
                    />
                )}

                <Panel
                    headerText={t("labels.headerText")}
                    isOpen={isConfigPanelOpen}
                    isBlocking={false}
                    onDismiss={() => setIsConfigPanelOpen(false)}
                    closeButtonAriaLabel={t("labels.closeButton")}
                    onRenderFooterContent={() => <DefaultButton onClick={() => setIsConfigPanelOpen(false)}>{t("labels.closeButton")}</DefaultButton>}
                    isFooterAtBottom={true}
                >
                    <Settings
                        promptTemplate={promptTemplate}
                        temperature={temperature}
                        retrieveCount={retrieveCount}
                        maxSubqueryCount={maxSubqueryCount}
                        resultsMergeStrategy={resultsMergeStrategy}
                        seed={seed}
                        minimumSearchScore={minimumSearchScore}
                        minimumRerankerScore={minimumRerankerScore}
                        useSemanticRanker={useSemanticRanker}
                        useSemanticCaptions={useSemanticCaptions}
                        useQueryRewriting={useQueryRewriting}
                        reasoningEffort={reasoningEffort}
                        excludeCategory={excludeCategory}
                        includeCategory={includeCategory}
                        retrievalMode={retrievalMode}
                        useGPT4V={useGPT4V}
                        gpt4vInput={gpt4vInput}
                        vectorFields={vectorFields}
                        showSemanticRankerOption={showSemanticRankerOption}
                        showQueryRewritingOption={showQueryRewritingOption}
                        showReasoningEffortOption={showReasoningEffortOption}
                        showGPT4VOptions={showGPT4VOptions}
                        showVectorOption={showVectorOption}
                        useOidSecurityFilter={useOidSecurityFilter}
                        useGroupsSecurityFilter={useGroupsSecurityFilter}
                        useLogin={!!useLogin}
                        loggedIn={loggedIn}
                        requireAccessControl={requireAccessControl}
                        shouldStream={shouldStream}
                        streamingEnabled={streamingEnabled}
                        useSuggestFollowupQuestions={useSuggestFollowupQuestions}
                        showSuggestFollowupQuestions={true}
                        showAgenticRetrievalOption={showAgenticRetrievalOption}
                        useAgenticRetrieval={useAgenticRetrieval}
                        onChange={handleSettingsChange}
                    />
                    {useLogin && <TokenClaimsDisplay />}
                </Panel>
            </div>
        </div>
    );
};

export default Chat;






